import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Descriptions,
  Input,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Typography
} from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import dayjs from 'dayjs';
import { DATE_FORMAT, FULL_DATE_FORMAT } from 'constants/dates';
import {
  cancelInvoice,
  fetchInvoice,
  fetchInvoicePdf,
  updateInvoiceNumber
} from 'services/api/invoicing';
import {
  INVOICE_DOCUMENT_TYPE_LABELS,
  INVOICE_PAYMENT_METHOD_LABELS,
  INVOICE_SERVICE_TYPE_LABELS,
  INVOICE_STATUS_COLORS,
  INVOICE_STATUS_LABELS,
  invoicePayUrl
} from 'constants/invoicing';
import {
  Invoice,
  InvoiceDocumentType,
  InvoiceLine,
  InvoicePaymentMethod,
  InvoiceStatus
} from 'types/api/invoicing';

import { showErrorMessage } from 'utils/showErrorMessage';
import { PageWrapper } from 'components/ui';
import { money } from 'utils/price';
import MarkPaidModal from '../Invoices/components/MarkPaidModal';
import CreditNoteModal from './components/CreditNoteModal';
import { confirmAndSendInvoice } from '../sendInvoiceWithToast';

const dateOrDash = (value?: string | null, format = FULL_DATE_FORMAT) =>
  value ? dayjs(value).format(format) : '—';

const InvoiceDetails: FC = () => {
  const { publicId = '' } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const { modal, message } = App.useApp();

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [lines, setLines] = useState<InvoiceLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfOpen, setPdfOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [creditNoteOpen, setCreditNoteOpen] = useState(false);

  const reload = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    if (!publicId) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchInvoice(publicId);

        if (cancelled) return;
        setInvoice(res.invoice);
        setLines(res.lines);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [publicId, refreshKey]);

  const onViewPdf = useCallback(async () => {
    try {
      const blob = await fetchInvoicePdf(publicId);
      setPdfUrl(URL.createObjectURL(blob));
      setPdfOpen(true);
    } catch (err) {
      showErrorMessage(err);
    }
  }, [publicId]);

  const closePdf = () => {
    setPdfOpen(false);

    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }

    setPdfUrl('');
  };

  const onCancel = useCallback(() => {
    if (!invoice) {
      return;
    }

    modal.confirm({
      title: `Cancel invoice ${invoice.invoiceNumber ?? ''}?`,
      okText: 'Cancel invoice',
      okButtonProps: { danger: true },
      cancelText: 'Close',
      onOk: async () => {
        try {
          await cancelInvoice(invoice.publicId);
          reload();
        } catch (err) {
          showErrorMessage(err);
        }
      }
    });
  }, [invoice, modal, reload]);

  const onSend = useCallback(() => {
    if (!invoice) {
      return;
    }

    confirmAndSendInvoice(String(invoice.id), {
      modal,
      message,
      invoiceNumber: invoice.invoiceNumber,
      email: invoice.patientEmail
    });
  }, [invoice, modal, message]);

  const currency = invoice?.currency;

  const lineColumns: ColumnProps<InvoiceLine>[] = useMemo(
    () => [
      {
        title: '#',
        key: 'index',
        width: 48,
        render: (_, __, index) => index + 1
      },
      {
        title: 'Type',
        dataIndex: 'lineType',
        key: 'lineType',
        render: (value: InvoiceLine['lineType']) =>
          INVOICE_SERVICE_TYPE_LABELS[value] ?? value
      },
      {
        title: 'Description',
        key: 'description',
        render: (_, row) => row.descriptionEn || row.descriptionDe || '—'
      },
      {
        title: 'Doctor',
        dataIndex: 'doctorName',
        key: 'doctorName',
        render: (value?: string | null) => value || '—'
      },
      {
        title: 'Qty',
        dataIndex: 'quantity',
        key: 'quantity',
        align: 'right',
        render: (value: string) => Number(value)
      },
      {
        title: 'Unit',
        dataIndex: 'unitAmount',
        key: 'unitAmount',
        align: 'right',
        render: (value: string) => money(value, { currency })
      },
      {
        // Shows why Unit and Line total differ (the BE applies the discount).
        title: 'Discount',
        key: 'discount',
        align: 'right',
        render: (_, row) => {
          const value = Number(row.discountValue ?? 0);

          if (!row.discountType || !value) {
            return '—';
          }

          return row.discountType === 'percent'
            ? `−${value}%`
            : `−${money(row.discountValue as string, { currency })}`;
        }
      },
      {
        title: 'Tax %',
        dataIndex: 'taxRate',
        key: 'taxRate',
        align: 'right',
        render: (value: string) => `${Number(value)}%`
      },
      {
        title: 'Line total',
        dataIndex: 'lineTotal',
        key: 'lineTotal',
        align: 'right',
        render: (value: string) => money(value, { currency })
      }
    ],
    [currency]
  );

  const isCreditNote = invoice?.documentType === InvoiceDocumentType.CreditNote;

  const docLabel = isCreditNote
    ? INVOICE_DOCUMENT_TYPE_LABELS[InvoiceDocumentType.CreditNote]
    : INVOICE_DOCUMENT_TYPE_LABELS[InvoiceDocumentType.Invoice];

  const canCancel =
    !!invoice &&
    !isCreditNote &&
    invoice.status !== InvoiceStatus.Paid &&
    invoice.status !== InvoiceStatus.Cancelled;

  // A credit note needs a real document to credit: never a draft (never
  // issued), a cancelled invoice (already void), or another credit note.
  const canCreditNote =
    !!invoice &&
    !isCreditNote &&
    (invoice.status === InvoiceStatus.Issued ||
      invoice.status === InvoiceStatus.Paid);

  // Manual mark-paid is allowed on any non-paid, non-cancelled invoice
  // (the BE permits it regardless of payment method).
  const canMarkPaid = canCancel;

  // Send/resend the invoice email — issued (PDF + pay link) or paid
  // (paid-invoice email). Drafts are rejected (400) by the BE.
  const canSend =
    !!invoice &&
    (invoice.status === InvoiceStatus.Issued ||
      invoice.status === InvoiceStatus.Paid);

  // Correct the human invoice number. Shown to every admin for now; the BE
  // still guards the endpoint with @Roles('super_admin') and answers 403,
  // which the error handler surfaces. The number is assigned at issuance, so
  // drafts are rejected by the BE either way.
  const canCorrectNumber = !!invoice && invoice.status !== InvoiceStatus.Draft;

  const onCorrectNumber = () => {
    if (!invoice) {
      return;
    }

    let value = invoice.invoiceNumber ?? '';

    modal.confirm({
      title: 'Correct invoice number',
      content: (
        <Input
          defaultValue={value}
          maxLength={40}
          placeholder="Invoice number"
          onChange={e => {
            value = e.target.value;
          }}
        />
      ),
      okText: 'Save',
      onOk: async () => {
        const next = value.trim();

        if (!next) {
          message.error('Enter an invoice number');

          return Promise.reject(new Error('empty'));
        }

        try {
          await updateInvoiceNumber(String(invoice.id), next);
          message.success('Invoice number updated');
          reload();
        } catch (err) {
          showErrorMessage(err);

          return Promise.reject(err);
        }

        return undefined;
      }
    });
  };

  return (
    <PageWrapper
      title={`${docLabel}${
        invoice?.invoiceNumber ? ` ${invoice.invoiceNumber}` : ''
      }`}
      color="white"
      extra={[
        <Button
          key="back"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/invoicing/invoices')}
        >
          Back to invoices
        </Button>,
        <Button key="pdf" onClick={onViewPdf} disabled={!invoice}>
          PDF
        </Button>,
        ...(canSend
          ? [
              <Button key="send" onClick={onSend}>
                Send
              </Button>
            ]
          : []),
        ...(canCorrectNumber
          ? [
              <Button key="correct-number" onClick={onCorrectNumber}>
                Correct number
              </Button>
            ]
          : []),
        ...(invoice &&
        !isCreditNote &&
        invoice.status === InvoiceStatus.Issued &&
        invoice.paymentMethod === InvoicePaymentMethod.Stripe
          ? [
              <Button
                key="paylink"
                onClick={() => {
                  copy(invoicePayUrl(invoice));
                  message.success('Payment link copied');
                }}
              >
                Copy pay link
              </Button>
            ]
          : []),
        ...(canMarkPaid
          ? [
              <Button key="mark" onClick={() => setMarkPaidOpen(true)}>
                Mark paid
              </Button>
            ]
          : []),
        ...(canCreditNote
          ? [
              <Button key="credit" onClick={() => setCreditNoteOpen(true)}>
                Credit note
              </Button>
            ]
          : []),
        ...(canCancel
          ? [
              <Button key="cancel" danger onClick={onCancel}>
                Cancel
              </Button>
            ]
          : [])
      ]}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : !invoice ? (
        <Typography.Text type="secondary">Invoice not found.</Typography.Text>
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label={`${docLabel} #`}>
              {invoice.invoiceNumber || '— (draft)'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={INVOICE_STATUS_COLORS[invoice.status]}>
                {INVOICE_STATUS_LABELS[invoice.status] ?? invoice.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="ID">{invoice.id}</Descriptions.Item>
            {!!invoice.creditsInvoiceId && (
              <Descriptions.Item label="Credits invoice" span={2}>
                {/* creditsInvoiceId is the internal id; the detail route
                    takes an internal id or a publicId. */}
                <Link
                  to={`/invoicing/invoices/${invoice.creditsInvoiceId}`}
                >{`#${invoice.creditsInvoiceId}`}</Link>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Hashed ID">
              <Typography.Text copyable={{ text: invoice.publicId }}>
                {invoice.publicId}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Patient">
              {/* patientId is null when the invoice is billed to a non-user
                  customer — there is no profile to open, so show plain text. */}
              {invoice.patientId ? (
                <Link
                  to={`/access/patients/${invoice.patientId}/personal-information`}
                >
                  {invoice.patientName}
                </Link>
              ) : (
                invoice.patientName
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Patient email">
              {invoice.patientEmail || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Payment method">
              {INVOICE_PAYMENT_METHOD_LABELS[invoice.paymentMethod] ??
                invoice.paymentMethod}
            </Descriptions.Item>
            <Descriptions.Item label="Currency">
              {invoice.currency}
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              {dateOrDash(invoice.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Issued">
              {dateOrDash(invoice.issuedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Due">
              {dateOrDash(invoice.dueDate, DATE_FORMAT)}
            </Descriptions.Item>
            <Descriptions.Item label="Paid">
              {dateOrDash(invoice.paidAt)}
            </Descriptions.Item>
            {invoice.receiptUrl && (
              <Descriptions.Item label="Receipt">
                <Typography.Link
                  href={invoice.receiptUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {invoice.paymentMethod === InvoicePaymentMethod.Stripe
                    ? 'View Stripe receipt'
                    : 'View receipt'}
                </Typography.Link>
              </Descriptions.Item>
            )}
          </Descriptions>

          {(invoice.billingName || invoice.billingLine1) && (
            <Descriptions title="Billing" bordered size="small" column={2}>
              <Descriptions.Item label="Name">
                {invoice.billingName || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Company">
                {invoice.billingCompany || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="VAT ID">
                {invoice.billingVatId || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Address" span={2}>
                {[
                  invoice.billingLine1,
                  invoice.billingLine2,
                  invoice.billingPostalCode,
                  invoice.billingCity,
                  invoice.billingState,
                  invoice.billingCountry
                ]
                  .filter(Boolean)
                  .join(', ') || '—'}
              </Descriptions.Item>
            </Descriptions>
          )}

          <Table
            dataSource={lines}
            columns={lineColumns}
            rowKey="publicId"
            pagination={false}
            size="small"
          />

          <Descriptions
            bordered
            size="small"
            column={1}
            style={{ maxWidth: 360 }}
          >
            <Descriptions.Item label="Subtotal">
              {money(invoice.subtotalAmount, { currency })}
            </Descriptions.Item>
            <Descriptions.Item label="Tax">
              {money(invoice.taxAmount, { currency })}
            </Descriptions.Item>
            <Descriptions.Item label="Total">
              <strong>{money(invoice.totalAmount, { currency })}</strong>
            </Descriptions.Item>
          </Descriptions>
        </Space>
      )}

      <Modal
        open={pdfOpen}
        onCancel={closePdf}
        footer={null}
        width={900}
        title="Invoice PDF"
      >
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            title="Invoice PDF"
            style={{ width: '100%', height: '75vh', border: 'none' }}
          />
        )}
      </Modal>

      <MarkPaidModal
        invoice={markPaidOpen ? invoice : null}
        onClose={() => setMarkPaidOpen(false)}
        onSuccess={reload}
      />

      {!!invoice && (
        <CreditNoteModal
          open={creditNoteOpen}
          invoice={invoice}
          onClose={() => setCreditNoteOpen(false)}
          onCreated={note => {
            setCreditNoteOpen(false);
            message.success(
              `Credit note ${note.invoiceNumber ?? ''} issued`.trim()
            );
            // Stay on the invoice and refresh it: the admin wants to see the
            // document they just corrected, and the note is reachable from
            // the credit-note list below.
            reload();
          }}
        />
      )}
    </PageWrapper>
  );
};

export default InvoiceDetails;
